import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import './AgentApp.less';
import { setCREditingDialogExpanded } from './app/Slices/changeRequestEditSlice';
import { useAgentDispatch } from './hooks/useAgentStore';
import { getProducts } from './app/ActionTypes/productActionTypes';
import ActionError from './utils/ActionError';
import { Notify } from './services/notificationService';
import AppSideMenu from './components/AppSideMenu/AppSideMenu';
import { Layout, Result, Button } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import IntercomChat from './components/IntercomChat/IntercomChat';
import { trackException } from './utils/monitoring';
import { SidebarMenuProvider } from './hooks/useSidebarMenuItem';
import { useEmailVerificationHandler } from './hooks/useEmailVerificationHandler';
import { useTrialActivationHandler } from './hooks/useTrialActivationHandler';
import { useCheckoutSuccessHandler } from './hooks/useCheckoutSuccessHandler';
import { CirraServiceType } from './enums/auth';
import ConfirmActivationModal from './components/ConfirmActivationModal/ConfirmActivationModal';
import { useSelector } from 'react-redux';
import { RootState } from './app/store';
import { TokenService } from './services/secureTokenService';
import { selectIsTeamOnlyWithNoTeams } from './app/Slices/teamSlice';
import { useLogout } from './hooks/useLogout';

const AgentApp: React.FC = () => {
  const location = useLocation();
  const agentDispatch = useAgentDispatch();
  const handleLogout = useLogout();
  const [collapsed, setCollapsed] = useState(false);
  const isTeamOnlyWithNoTeams = useSelector(selectIsTeamOnlyWithNoTeams);
  const userEmail = useSelector(
    (state: RootState) => state.team.personalAccount.record?.cirra_user_id
  );

  // Handle Auth0 email verification redirect
  useEmailVerificationHandler({ service: CirraServiceType.AGENTS });

  // Handle Stripe trial activation redirect (after adding payment method via portal)
  const { activationState, confirmActivation, cancelActivation } =
    useTrialActivationHandler();

  // Handle Stripe Checkout redirect (after completing checkout for new subscription)
  useCheckoutSuccessHandler();

  // Load products so ConfirmActivationModal can resolve price data
  useEffect(() => {
    agentDispatch(getProducts());
  }, []);

  useEffect(() => {
    const handleError = (error: ActionError) => {
      console.error('AgentApp Error:', error);
      trackException(error, { component: 'AgentApp' });
      Notify.error(error.message);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = new ActionError(
        'Unhandled promise rejection in AgentApp',
        event.reason
      );
      handleError(error);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  useEffect(() => {
    agentDispatch(setCREditingDialogExpanded(false));
  }, [location.pathname, agentDispatch]);

  const toggleCollapsed = (newCollapsed: boolean) => {
    setCollapsed(newCollapsed);
  };

  if (isTeamOnlyWithNoTeams) {
    return (
      <Layout className="agent-app-layout">
        <Header
          hasMenu={false}
          collapsed={collapsed}
          withOrgs={false}
          toggleCollapsed={toggleCollapsed}
        />
        <Layout>
          <Layout.Content className="agent-app-content">
            <Result
              icon={<TeamOutlined />}
              title="No Team Access"
              subTitle={`You are not a member of any teams. Please contact your team administrator to request access. Logged in as ${TokenService.getUserEmail() || userEmail || ''}.`}
              extra={
                <Button type="primary" onClick={handleLogout}>
                  Log Out
                </Button>
              }
            />
          </Layout.Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <SidebarMenuProvider>
      <Layout className="agent-app-layout">
        <Header
          collapsed={collapsed}
          withOrgs={true}
          toggleCollapsed={toggleCollapsed}
        />
        <Layout>
          <AppSideMenu collapsed={collapsed} />
          <Layout.Content className="agent-app-content">
            <Outlet />
          </Layout.Content>
        </Layout>
        <IntercomChat />

        {activationState.priceId && (
          <ConfirmActivationModal
            open={activationState.showConfirmation}
            priceId={activationState.priceId}
            onConfirm={confirmActivation}
            onCancel={cancelActivation}
            loading={activationState.confirming}
          />
        )}
      </Layout>
    </SidebarMenuProvider>
  );
};

export default AgentApp;
